import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CheckCircle, ExternalLink, Image as ImageLucide, Instagram, Lock, ArrowLeft, User, Download, Play, Music, X } from 'lucide-react';
import { stalkerAPI, getProxyUrl, getStaticFileUrl } from '../../services/api';
import { useDarkMode } from '../../contexts/DarkModeContext';
import { useSearch } from '../../contexts/SearchContext';

type TimeRangeKey = '7d' | '30d' | '3m' | '6m' | '1y';

const RANGE_LABELS: Record<TimeRangeKey, string> = {
	'7d': '7D',
	'30d': '30D',
	'3m': '3M',
	'6m': '6M',
	'1y': '1Y',
};

const rangeToStartDate = (key: TimeRangeKey): string => {
	const now = new Date();
	const d = new Date(now);
	switch (key) {
		case '7d':
			d.setDate(d.getDate() - 7);
			break;
		case '30d':
			d.setDate(d.getDate() - 30);
			break;
		case '3m':
			d.setMonth(d.getMonth() - 3);
			break;
		case '6m':
			d.setMonth(d.getMonth() - 6);
			break;
		case '1y':
			d.setFullYear(d.getFullYear() - 1);
			break;
	}
	return d.toISOString();
};

type PastLogPoint = {
	created_at: string;
	value: number | null;
	rawOld: any;
	rawNew: any;
};

const getFieldFromChangeType = (changeType: string) => {
	const t = (changeType || '').trim();
	if (!t.endsWith('_changed')) return null;
	return t.slice(0, -'_changed'.length);
};

const pickValue = (val: any, field: string | null) => {
	if (field && val && typeof val === 'object' && field in val) return val[field];
	return val;
};

const toNumberOrNull = (v: any): number | null => {
	if (v === null || v === undefined) return null;
	const n = typeof v === 'number' ? v : Number(v);
	return Number.isFinite(n) ? n : null;
};

const buildSeries = (logs: any[], changeType: string): PastLogPoint[] => {
	const field = getFieldFromChangeType(changeType);
	return logs
		.filter((l) => l.sl_changeType === changeType)
		.map((l) => {
			const oldRaw = pickValue(l.sl_oldValue, field);
			const newRaw = pickValue(l.sl_newValue, field);
			const val = toNumberOrNull(newRaw ?? oldRaw);
			return {
				created_at: l.created_at,
				value: val,
				rawOld: oldRaw,
				rawNew: newRaw,
			};
		})
		.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
};

const MiniLineChart: React.FC<{
	points: PastLogPoint[];
	color?: string;
	defaultLabelValue?: number | null;
	defaultLabelDate?: string | null;
}> = ({ points, color = '#E50914', defaultLabelValue, defaultLabelDate }) => {
	const [hoverIndex, setHoverIndex] = useState<number | null>(null);

	const realPoints = points.filter((p) => p.value !== null);

	let processedPoints = points;
	let values = processedPoints.map((p) => p.value).filter((v): v is number => v !== null);

	// Veri yoksa bile düz çizgi göstermek için sahte veri üret
	if (!processedPoints.length || !values.length) {
		const nowIso = new Date().toISOString();
		const fallbackVal = defaultLabelValue ?? 0;
		const baseDate = defaultLabelDate || nowIso;
		processedPoints = [
			{ created_at: baseDate, value: fallbackVal, rawOld: null, rawNew: null },
			{ created_at: baseDate, value: fallbackVal, rawOld: null, rawNew: null },
		];
		values = [fallbackVal];
	}

	// Eğer gerçek veri tek bir nokta ise, çizgiyi x ekseninde sonuna kadar uzatmak için
	// aynı değere sahip ikinci bir nokta ekleyelim (baş ve son)
	if (realPoints.length === 1 && processedPoints.length === 1) {
		const single = processedPoints[0];
		processedPoints = [
			single,
			{ ...single },
		];
		values = [single.value ?? 0, single.value ?? 0];
	}

	let min = Math.min(...values);
	let max = Math.max(...values);
	// Eğer tek değer varsa min/max/orta'yı value-1, value, value+1 yap
	if (min === max) {
		const v = min;
		min = v - 1;
		max = v + 1;
	}
	const span = max - min || 1;
	const width = 400;
	const height = 120;
	const paddingLeft = 36;
	const paddingRight = 8;
	const paddingTop = 8;
	const paddingBottom = 18;
	const innerWidth = width - paddingLeft - paddingRight;
	const innerHeight = height - paddingTop - paddingBottom;

	const pointsWithCoords = processedPoints.map((p, idx) => {
		const value = p.value ?? 0;
		const x = paddingLeft + (idx / Math.max(processedPoints.length - 1, 1)) * innerWidth;
		const normY = (value - min) / span;
		const y = paddingTop + (1 - normY) * innerHeight;
		return { ...p, x, y, value };
	});

	const d = pointsWithCoords.map((p) => `${p.x},${p.y}`).join(' ');

	const handleMouseMove = (e: React.MouseEvent<SVGSVGElement>) => {
		const svg = e.currentTarget;
		const rect = svg.getBoundingClientRect();
		const relX = ((e.clientX - rect.left) / rect.width) * width;
		let closestIndex = 0;
		let closestDist = Infinity;
		pointsWithCoords.forEach((p, idx) => {
			const dist = Math.abs(p.x - relX);
			if (dist < closestDist) {
				closestDist = dist;
				closestIndex = idx;
			}
		});
		setHoverIndex(closestIndex);
	};

	const handleMouseLeave = () => {
		setHoverIndex(null);
	};

	const mid = min + (max - min) / 2;
	const formatNumber = (n: number) => n.toLocaleString('tr-TR');

	// Hover yokken son gerçek veri; o da yoksa defaultLabelValue; en son çizgideki nokta
	const lastReal = realPoints.length ? realPoints[realPoints.length - 1] : null;
	const baseLabel =
		lastReal ||
		(defaultLabelValue != null
			? { value: defaultLabelValue, created_at: defaultLabelDate || new Date().toISOString() }
			: pointsWithCoords[pointsWithCoords.length - 1]);

	const hovered = hoverIndex !== null ? pointsWithCoords[hoverIndex] : null;
	const display = hovered || baseLabel;

	return (
		<div className="w-full">
			<svg
				viewBox={`0 0 ${width} ${height}`}
				className="w-full h-32"
				onMouseMove={handleMouseMove}
				onMouseLeave={handleMouseLeave}
			>
				{/* Y ekseni etiketleri */}
				<text x={2} y={paddingTop + 8} fontSize="10" fill="#9CA3AF">
					{formatNumber(max)}
				</text>
				<text x={2} y={paddingTop + innerHeight / 2 + 3} fontSize="10" fill="#9CA3AF">
					{formatNumber(mid)}
				</text>
				<text x={2} y={paddingTop + innerHeight + 8} fontSize="10" fill="#9CA3AF">
					{formatNumber(min)}
				</text>

				{/* Çizgi */}
				<polyline
					fill="none"
					stroke={color}
					strokeWidth="2"
					points={d}
					strokeLinecap="round"
					strokeLinejoin="round"
				/>

				{/* Hover dikey çizgi ve nokta */}
				{hovered && (
					<>
						<line
							x1={hovered.x}
							y1={paddingTop}
							x2={hovered.x}
							y2={paddingTop + innerHeight}
							stroke="#9CA3AF"
							strokeWidth={1}
							strokeDasharray="4 4"
						/>
						<circle cx={hovered.x} cy={hovered.y} r={3} fill={color} />
					</>
				)}
			</svg>
			{display && (
				<div className="mt-1 text-xs text-gray-600 dark:text-gray-300">
					<span className="font-semibold">{formatNumber(display.value ?? 0)}</span>{' '}
					<span className="text-gray-400 dark:text-gray-500">
						({new Date(display.created_at).toLocaleString('tr-TR')})
					</span>
				</div>
			)}
		</div>
	);
};

const SocialDetail: React.FC = () => {
	const { isDark } = useDarkMode();
	const { globalSearchTerm } = useSearch();
	const navigate = useNavigate();
	const { platform, id } = useParams<{ platform: string; id: string }>();

	const [loading, setLoading] = useState(true);
	const [socialData, setSocialData] = useState<any | null>(null);
	const [mediaLoading, setMediaLoading] = useState(false);
	const [medias, setMedias] = useState<any[]>([]);
	const [mediaPage, setMediaPage] = useState(1);
	const [mediaHasMore, setMediaHasMore] = useState(true);
	const mediaLoadSentinelRef = useRef<HTMLDivElement | null>(null);
	const mediaPageRef = useRef(1);
	const mediaHasMoreRef = useRef(true);
	const mediaLoadingRef = useRef(false);
	const [range, setRange] = useState<TimeRangeKey>('30d');
	const [logsFollower, setLogsFollower] = useState<any[]>([]);
	const [logsFollowing, setLogsFollowing] = useState<any[]>([]);
	const [logsMedia, setLogsMedia] = useState<any[]>([]);
	const [logsUsername, setLogsUsername] = useState<any[]>([]);
	const [logsPrivate, setLogsPrivate] = useState<any[]>([]);
	const [lightboxImage, setLightboxImage] = useState<string | null>(null);
	const [lightboxType, setLightboxType] = useState<'image' | 'video'>('image');
	const [lightboxMedia, setLightboxMedia] = useState<any | null>(null);
	const [zoomLevel, setZoomLevel] = useState(1);
	const [imagePosition, setImagePosition] = useState({ x: 0, y: 0 });
	const [isDragging, setIsDragging] = useState(false);
	const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

	useEffect(() => {
		const loadSocial = async () => {
			try {
				if (!platform || !id) return;
				setLoading(true);
				const resp = await stalkerAPI.socials.getById(platform, Number(id));
				setSocialData(resp.data.data);
			} catch (err) {
				console.error('Error loading social detail', err);
			} finally {
				setLoading(false);
			}
		};
		loadSocial();
	}, [platform, id]);

	const loadMediaPage = useCallback(async (page: number) => {
		if (!platform || !id) return;
		if (mediaLoadingRef.current) return;
		if (!mediaHasMoreRef.current && page > 1) return;
		mediaLoadingRef.current = true;
		setMediaLoading(true);
		try {
			const resp = await stalkerAPI.socials.getMedia(platform, Number(id), { page, limit: 24 });
			const payload = resp.data?.data;
			const list = Array.isArray(payload?.media) ? payload.media : Array.isArray(payload) ? payload : [];
			const pagination = payload?.pagination;
			const hasMore = pagination ? page < pagination.totalPages : list.length === 24;
			mediaHasMoreRef.current = hasMore;
			setMediaHasMore(hasMore);
			if (page === 1) {
				setMedias(list);
			} else {
				setMedias(prev => [...prev, ...list]);
			}
		} catch (err) {
			console.error('Error loading social medias', err);
		} finally {
			mediaLoadingRef.current = false;
			setMediaLoading(false);
		}
	}, [platform, id]);

	useEffect(() => {
		// Reset and load first page when platform/id changes
		setMedias([]);
		setMediaPage(1);
		setMediaHasMore(true);
		mediaPageRef.current = 1;
		mediaHasMoreRef.current = true;
		mediaLoadingRef.current = false;
		loadMediaPage(1);
	}, [platform, id, loadMediaPage]);

	// Intersection Observer for infinite scroll
	useEffect(() => {
		const sentinel = mediaLoadSentinelRef.current;
		if (!sentinel) return;
		const observer = new IntersectionObserver(
			(entries) => {
				if (entries[0].isIntersecting && mediaHasMoreRef.current && !mediaLoadingRef.current) {
					const nextPage = mediaPageRef.current + 1;
					mediaPageRef.current = nextPage;
					setMediaPage(nextPage);
					loadMediaPage(nextPage);
				}
			},
			{ rootMargin: '200px' }
		);
		observer.observe(sentinel);
		return () => observer.disconnect();
	}, [loadMediaPage, mediaHasMore, mediaLoading]);

	// Lightbox açıkken sayfa scroll'unu kilitle
	useEffect(() => {
		if (!lightboxImage) return;
		const originalOverflow = document.body.style.overflow;
		document.body.style.overflow = 'hidden';
		return () => {
			document.body.style.overflow = originalOverflow;
		};
	}, [lightboxImage]);

	useEffect(() => {
		const loadLogs = async () => {
			try {
				if (!socialData || (platform || '').toLowerCase() !== 'instagram') return;
				const accountId = socialData.iu_personId;
				if (!accountId) return;

				const startDate = rangeToStartDate(range);

				const baseParams = {
					accountId: Number(id),
					platform: 'instagram',
					startDate,
					order: 'ASC' as const,
				};

				const [
					followerResp,
					followingResp,
					mediaResp,
					usernameResp,
					privateResp,
				] = await Promise.all([
					stalkerAPI.logs.getPast({ ...baseParams, changeType: 'followerCount_changed' }),
					stalkerAPI.logs.getPast({ ...baseParams, changeType: 'followingCount_changed' }),
					stalkerAPI.logs.getPast({ ...baseParams, changeType: 'mediaCount_changed' }),
					stalkerAPI.logs.getPast({ ...baseParams, changeType: 'username_changed' }),
					stalkerAPI.logs.getPast({ ...baseParams, changeType: 'isPrivate_changed' }),
				]);

				setLogsFollower(followerResp.data.data || []);
				setLogsFollowing(followingResp.data.data || []);
				setLogsMedia(mediaResp.data.data || []);
				setLogsUsername(usernameResp.data.data || []);
				setLogsPrivate(privateResp.data.data || []);
			} catch (err) {
				console.error('Error loading past logs', err);
			}
		};
		loadLogs();
	}, [socialData, platform, range]);

	const isInstagram = useMemo(
		() => (platform || '').toLowerCase() === 'instagram',
		[platform]
	);

	const isTiktok = useMemo(
		() => (platform || '').toLowerCase() === 'tiktok',
		[platform]
	);

	const isVsco = useMemo(
		() => (platform || '').toLowerCase() === 'vsco',
		[platform]
	);

	const followerSeries = useMemo(
		() => buildSeries(logsFollower, 'followerCount_changed'),
		[logsFollower]
	);
	const followingSeries = useMemo(
		() => buildSeries(logsFollowing, 'followingCount_changed'),
		[logsFollowing]
	);
	const mediaSeries = useMemo(
		() => buildSeries(logsMedia, 'mediaCount_changed'),
		[logsMedia]
	);

	const handleLightboxOpen = (mediaUrl: string, mediaType: 'image' | 'video', media?: any) => {
		setLightboxImage(mediaUrl);
		setLightboxType(mediaType);
		setLightboxMedia(media || null);
		setZoomLevel(1);
		setImagePosition({ x: 0, y: 0 });
	};

	const handleLightboxClose = () => {
		setLightboxImage(null);
		setLightboxMedia(null);
		setZoomLevel(1);
		setImagePosition({ x: 0, y: 0 });
	};

	const handleZoom = (delta: number) => {
		setZoomLevel(prev => Math.max(0.5, Math.min(5, prev + delta)));
	};

	const handleMouseDown = (e: React.MouseEvent) => {
		if (zoomLevel > 1) {
			setIsDragging(true);
			setDragStart({ x: e.clientX - imagePosition.x, y: e.clientY - imagePosition.y });
		}
	};

	const handleMouseMove = (e: React.MouseEvent) => {
		if (isDragging && zoomLevel > 1) {
			setImagePosition({
				x: e.clientX - dragStart.x,
				y: e.clientY - dragStart.y
			});
		}
	};

	const handleMouseUp = () => {
		setIsDragging(false);
	};

	const handleWheel = (e: React.WheelEvent) => {
		if (lightboxImage) {
			e.preventDefault();
			const delta = e.deltaY > 0 ? -0.1 : 0.1;
			handleZoom(delta);
		}
	};

	if (loading) {
		return (
			<div className="flex items-center justify-center h-64">
				<div
					className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin"
					style={{ borderColor: '#E50914' }}
				/>
			</div>
		);
	}

	if (!socialData) {
		return (
			<div className="text-center py-12">
				<h3 className={`text-lg font-medium mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>Social Not Found</h3>
				<button
					onClick={() => navigate('/stalker/socials')}
					className="mt-4 px-4 py-2 rounded-lg text-white hover:opacity-90 transition-opacity"
					style={{ backgroundColor: '#E50914' }}
				>
					Back to Socials
				</button>
			</div>
		);
	}

	return (
		<div>
			<button
				onClick={() => navigate(-1)}
				className={`flex items-center space-x-2 font-mono text-[11px] mb-4 tracking-widest uppercase transition-colors ${isDark ? 'text-neutral-500 hover:text-emerald-500' : 'text-neutral-500 hover:text-emerald-600'}`}
			>
				<ArrowLeft className="w-3.5 h-3.5" />
				<span>// BACK_TO_INTEL</span>
			</button>

			<div className={`p-6 border rounded-none ${isDark ? 'bg-[#111] border-[#222]' : 'bg-white border-neutral-200'} shadow-none`}>
				{isInstagram ? (
					<>
						{/* Header */}
						<div className="flex items-start justify-between flex-col md:flex-row gap-4 border-b pb-6 mb-6 border-dashed border-[#333]">
							<div className="flex items-center space-x-6">
								{socialData.iu_profilePicUrl ? (
									<img
										src={getProxyUrl(socialData.iu_profilePicUrl) || ''}
										alt={socialData.iu_username}
										className={`w-20 h-20 object-cover border cursor-pointer hover:opacity-80 transition-all ${isDark ? 'border-[#444]' : 'border-neutral-300'}`}
										onClick={() => handleLightboxOpen(getProxyUrl(socialData.iu_profilePicUrl) || '', 'image')}
									/>
								) : (
									<div className={`w-20 h-20 flex items-center justify-center border ${isDark ? 'bg-[#1a1a1a] border-[#333]' : 'bg-neutral-100 border-neutral-200'}`}>
										<Instagram className={`w-10 h-10 ${isDark ? 'text-neutral-700' : 'text-neutral-300'}`} />
									</div>
								)}
								<div>
									<div className="flex items-center space-x-2">
										<h2 className={`text-xl md:text-2xl font-mono font-bold tracking-widest uppercase ${isDark ? 'text-neutral-200' : 'text-neutral-800'}`}>
											@{socialData.iu_username}
										</h2>
										{socialData.iu_isVerified === '1' && (
											<CheckCircle className="w-4 h-4 md:w-5 md:h-5 text-blue-500 flex-shrink-0" strokeWidth={2.5} />
										)}
										{socialData.iu_isPrivate === '1' && (
											<Lock className={`w-4 h-4 md:w-5 md:h-5 flex-shrink-0 ${isDark ? 'text-neutral-500' : 'text-neutral-400'}`} />
										)}
									</div>
									{socialData.iu_name && (
										<p className={`text-[12px] font-mono mt-1 ${isDark ? 'text-emerald-500/70' : 'text-emerald-600/70'}`}>{socialData.iu_name}</p>
									)}
								</div>
							</div>
							<div className="flex items-center space-x-2">
								<a
									href={`https://instagram.com/${socialData.iu_username}`}
									target="_blank"
									rel="noopener noreferrer"
									className={`flex items-center justify-center w-10 h-10 border transition-all ${isDark ? 'bg-[#0a0a0a] border-[#333] hover:border-emerald-500/50 text-neutral-500' : 'bg-neutral-50 border-neutral-300 hover:border-emerald-500/50 text-neutral-600'}`}
								>
									<ExternalLink className="w-4 h-4" />
								</a>
							</div>
						</div>

						{/* Stats */}
						<div className="grid grid-cols-1 md:grid-cols-4 gap-4">
							<div className={`p-4 border rounded-none ${isDark ? 'bg-[#0a0a0a] border-[#222]' : 'bg-neutral-50 border-neutral-200'}`}>
								<div className="flex items-center space-x-2 mb-1">
									<span className={`text-[10px] font-mono tracking-widest uppercase ${isDark ? 'text-neutral-500' : 'text-neutral-500'}`}>FOLLOWERS</span>
								</div>
								<p className={`text-2xl font-mono font-bold ${isDark ? 'text-neutral-200' : 'text-neutral-800'}`}>
									{socialData.iu_followerCount?.toLocaleString() || '0'}
								</p>
							</div>
							<div className={`p-4 border rounded-none ${isDark ? 'bg-[#0a0a0a] border-[#222]' : 'bg-neutral-50 border-neutral-200'}`}>
								<div className="flex items-center space-x-2 mb-1">
									<span className={`text-[10px] font-mono tracking-widest uppercase ${isDark ? 'text-neutral-500' : 'text-neutral-500'}`}>FOLLOWING</span>
								</div>
								<p className={`text-2xl font-mono font-bold ${isDark ? 'text-neutral-200' : 'text-neutral-800'}`}>
									{socialData.iu_followingCount?.toLocaleString() || '0'}
								</p>
							</div>
							<div className={`p-4 border rounded-none ${isDark ? 'bg-[#0a0a0a] border-[#222]' : 'bg-neutral-50 border-neutral-200'}`}>
								<div className="flex items-center space-x-2 mb-1">
									<span className={`text-[10px] font-mono tracking-widest uppercase ${isDark ? 'text-neutral-500' : 'text-neutral-500'}`}>MEDIA</span>
								</div>
								<p className={`text-2xl font-mono font-bold ${isDark ? 'text-neutral-200' : 'text-neutral-800'}`}>
									{socialData.iu_mediaCount?.toLocaleString() || '0'}
								</p>
							</div>
							{socialData.iu_personId && (
								<div
									onClick={() => navigate(`/stalker/persons/${socialData.iu_personId}`)}
									className={`p-4 border rounded-none cursor-pointer flex flex-col justify-between transition-colors ${isDark ? 'bg-[#0a0a0a] border-[#222] hover:border-emerald-500/30' : 'bg-neutral-50 border-neutral-200 hover:border-emerald-500/30'}`}
								>
									<div className="flex items-center space-x-2 mb-2">
										<span className={`text-[10px] font-mono tracking-widest uppercase ${isDark ? 'text-neutral-500' : 'text-neutral-500'}`}>ENTITY_REF</span>
									</div>
									<div className="flex items-center space-x-3">
										{socialData.person_avatar ? (
											<img
												src={getStaticFileUrl(socialData.person_avatar) || ''}
												alt="AVT"
												className={`w-8 h-8 object-cover border ${isDark ? 'border-[#333]' : 'border-neutral-200'}`}
											/>
										) : (
											<div className={`w-8 h-8 flex items-center justify-center border ${isDark ? 'bg-[#111] border-[#333]' : 'bg-white border-neutral-200'}`}>
												<User className={`w-4 h-4 ${isDark ? 'text-neutral-700' : 'text-neutral-300'}`} />
											</div>
										)}
										<div className="min-w-0 flex-1">
											<p className={`text-[11px] font-mono font-bold uppercase truncate ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>
												{socialData.person_name} {socialData.person_surname}
											</p>
										</div>
									</div>
								</div>
							)}
						</div>

						{/* Time range filter */}
						<div className="mt-8 flex flex-wrap gap-2">
							{(Object.keys(RANGE_LABELS) as TimeRangeKey[]).map((key) => (
								<button
									key={key}
									type="button"
									onClick={() => setRange(key)}
									className={`px-3 py-1 text-[10px] font-mono tracking-widest uppercase border transition-colors ${range === key
										? 'bg-emerald-500/20 border-emerald-500/50 text-emerald-400'
										: isDark
											? 'bg-[#0a0a0a] border-[#333] text-neutral-500 hover:text-neutral-300'
											: 'bg-neutral-50 border-neutral-300 text-neutral-500 hover:text-neutral-800'
										}`}
								>
									{RANGE_LABELS[key]}
								</button>
							))}
						</div>

						{/* Charts */}
						<div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
							<div className={`p-4 border rounded-none ${isDark ? 'bg-[#0a0a0a] border-[#222]' : 'bg-neutral-50 border-neutral-200'}`}>
								<h3 className={`text-[10px] font-mono tracking-widest uppercase mb-4 pb-2 border-b ${isDark ? 'text-emerald-500/50 border-[#222]' : 'text-emerald-600/50 border-neutral-200'}`}>
									// FOLLOWERS_HISTORY
								</h3>
								<MiniLineChart
									points={followerSeries}
									color="#22c55e"
									defaultLabelValue={socialData.iu_followerCount ?? null}
									defaultLabelDate={socialData.created_at ?? null}
								/>
							</div>
							<div className={`p-4 border rounded-none ${isDark ? 'bg-[#0a0a0a] border-[#222]' : 'bg-neutral-50 border-neutral-200'}`}>
								<h3 className={`text-[10px] font-mono tracking-widest uppercase mb-4 pb-2 border-b ${isDark ? 'text-emerald-500/50 border-[#222]' : 'text-emerald-600/50 border-neutral-200'}`}>
									// FOLLOWING_HISTORY
								</h3>
								<MiniLineChart
									points={followingSeries}
									color="#3b82f6"
									defaultLabelValue={socialData.iu_followingCount ?? null}
									defaultLabelDate={socialData.created_at ?? null}
								/>
							</div>
							<div className={`p-4 border rounded-none ${isDark ? 'bg-[#0a0a0a] border-[#222]' : 'bg-neutral-50 border-neutral-200'}`}>
								<h3 className={`text-[10px] font-mono tracking-widest uppercase mb-4 pb-2 border-b ${isDark ? 'text-emerald-500/50 border-[#222]' : 'text-emerald-600/50 border-neutral-200'}`}>
									// MEDIA_HISTORY
								</h3>
								<MiniLineChart
									points={mediaSeries}
									color="#eab308"
									defaultLabelValue={socialData.iu_mediaCount ?? null}
									defaultLabelDate={socialData.created_at ?? null}
								/>
							</div>
						</div>

						{/* Username + Private logs */}
						<div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
							<div className={`p-4 border rounded-none ${isDark ? 'bg-[#0a0a0a] border-[#222]' : 'bg-neutral-50 border-neutral-200'}`}>
								<h3 className={`text-[10px] font-mono font-bold tracking-widest uppercase mb-4 pb-2 border-b ${isDark ? 'text-neutral-400 border-[#222]' : 'text-neutral-600 border-neutral-200'}`}>// IDENTIFIER_LOGS</h3>
								{(logsUsername || []).length === 0 ? (
									<p className={`text-[9px] font-mono uppercase tracking-widest ${isDark ? 'text-neutral-600' : 'text-neutral-400'}`}>[ NO_MODIFICATIONS_DETECTED ]</p>
								) : (
									<ul className="space-y-1 text-sm">
										{logsUsername.map((log: any, idx: number) => {
											const field = getFieldFromChangeType('username_changed');
											const oldVal = pickValue(log.sl_oldValue, field);
											const newVal = pickValue(log.sl_newValue, field);
											return (
												<li key={idx} className={`text-[10px] font-mono tracking-wider ${isDark ? 'text-neutral-400' : 'text-neutral-600'}`}>
													<span className="text-emerald-500/80">@{oldVal}</span>{' '}
													<span className={isDark ? 'text-neutral-600' : 'text-neutral-400'}>→</span>{' '}
													<span className="text-emerald-500">@{newVal}</span>{' '}
													<span className={`text-[9px] ${isDark ? 'text-neutral-600' : 'text-neutral-500'}`}>
														[{new Date(log.created_at).toLocaleString('tr-TR')}]
													</span>
												</li>
											);
										})}
									</ul>
								)}
							</div>
							<div className={`p-4 border rounded-none ${isDark ? 'bg-[#0a0a0a] border-[#222]' : 'bg-neutral-50 border-neutral-200'}`}>
								<h3 className={`text-[10px] font-mono font-bold tracking-widest uppercase mb-4 pb-2 border-b ${isDark ? 'text-neutral-400 border-[#222]' : 'text-neutral-600 border-neutral-200'}`}>// SECURITY_AUDIT</h3>
								{(logsPrivate || []).length === 0 ? (
									<p className={`text-[9px] font-mono uppercase tracking-widest ${isDark ? 'text-neutral-600' : 'text-neutral-400'}`}>[ STABLE_STATE ]</p>
								) : (
									<ul className="space-y-1 text-sm">
										{logsPrivate.map((log: any, idx: number) => {
											const field = getFieldFromChangeType('isPrivate_changed');
											const oldVal = String(pickValue(log.sl_oldValue, field) ?? '');
											const newVal = String(pickValue(log.sl_newValue, field) ?? '');
											const oldLocked = oldVal === '1';
											const newLocked = newVal === '1';
											return (
												<li key={idx} className={`text-[10px] font-mono tracking-wider ${isDark ? 'text-neutral-400' : 'text-neutral-600'}`}>
													<span>{oldLocked ? '🔒' : '🔓'}</span>{' '}
													<span className={isDark ? 'text-neutral-600' : 'text-neutral-400'}>→</span>{' '}
													<span>{newLocked ? '🔒' : '🔓'}</span>{' '}
													<span className={`text-[9px] ${isDark ? 'text-neutral-600' : 'text-neutral-500'}`}>
														[{new Date(log.created_at).toLocaleString('tr-TR')}]
													</span>
												</li>
											);
										})}
									</ul>
								)}
							</div>
						</div>

						{/* Bio */}
						{socialData.iu_bio && (
							<div className="mt-8">
								<h3 className={`text-[10px] font-mono tracking-widest uppercase mb-2 ${isDark ? 'text-emerald-500' : 'text-emerald-700'}`}>// BIO_DECRYPTED</h3>
								<p className={`text-[11px] font-mono leading-relaxed ${isDark ? 'text-neutral-400' : 'text-neutral-600'}`}>{socialData.iu_bio}</p>
							</div>
						)}
					</>
				) : isTiktok ? (
					<>
						{/* Header - TikTok */}
						<div className="flex items-start justify-between flex-col md:flex-row gap-4 border-b pb-6 mb-6 border-dashed border-[#333]">
							<div className="flex items-center space-x-6">
								{socialData.tu_avatarUrl ? (
									<img
										src={getProxyUrl(socialData.tu_avatarUrl) || socialData.tu_avatarUrl || ''}
										alt={socialData.tu_username}
										className={`w-20 h-20 object-cover border cursor-pointer hover:opacity-80 transition-all ${isDark ? 'border-[#444]' : 'border-neutral-300'}`}
										onClick={() => handleLightboxOpen(getProxyUrl(socialData.tu_avatarUrl) || socialData.tu_avatarUrl || '', 'image')}
									/>
								) : (
									<div className={`w-20 h-20 flex items-center justify-center border ${isDark ? 'bg-[#1a1a1a] border-[#333]' : 'bg-neutral-100 border-neutral-200'}`}>
										<ImageLucide className={`w-10 h-10 ${isDark ? 'text-neutral-700' : 'text-neutral-300'}`} />
									</div>
								)}
								<div>
									<div className="flex items-center space-x-2">
										<h2 className={`text-xl md:text-2xl font-mono font-bold tracking-widest uppercase ${isDark ? 'text-neutral-200' : 'text-neutral-800'}`}>
											@{socialData.tu_username}
										</h2>
										{(socialData.tu_verified === '1' || socialData.tu_verified === true) && (
											<CheckCircle className="w-4 h-4 md:w-5 md:h-5 text-blue-500 flex-shrink-0" strokeWidth={2.5} />
										)}
										{socialData.tu_user_isPrivate === '1' && (
											<Lock className={`w-4 h-4 md:w-5 md:h-5 flex-shrink-0 ${isDark ? 'text-neutral-500' : 'text-neutral-400'}`} />
										)}
									</div>
									{socialData.tu_name && (
										<p className={`text-[12px] font-mono mt-1 ${isDark ? 'text-emerald-500/70' : 'text-emerald-600/70'}`}>{socialData.tu_name}</p>
									)}
								</div>
							</div>
							<div className="flex items-center space-x-2">
								<a
									href={`https://tiktok.com/@${socialData.tu_username}`}
									target="_blank"
									rel="noopener noreferrer"
									className={`flex items-center justify-center w-10 h-10 border transition-all ${isDark ? 'bg-[#0a0a0a] border-[#333] hover:border-emerald-500/50 text-neutral-500' : 'bg-neutral-50 border-neutral-300 hover:border-emerald-500/50 text-neutral-600'}`}
								>
									<ExternalLink className="w-4 h-4" />
								</a>
							</div>
						</div>

						{/* Stats - TikTok */}
						<div className="grid grid-cols-1 md:grid-cols-4 gap-4">
							<div className={`p-4 border rounded-none ${isDark ? 'bg-[#0a0a0a] border-[#222]' : 'bg-neutral-50 border-neutral-200'}`}>
								<div className="flex items-center space-x-2 mb-1">
									<span className={`text-[10px] font-mono tracking-widest uppercase ${isDark ? 'text-neutral-500' : 'text-neutral-500'}`}>FOLLOWERS</span>
								</div>
								<p className={`text-2xl font-mono font-bold ${isDark ? 'text-neutral-200' : 'text-neutral-800'}`}>
									{socialData.tu_followerCount?.toLocaleString() || '0'}
								</p>
							</div>
							<div className={`p-4 border rounded-none ${isDark ? 'bg-[#0a0a0a] border-[#222]' : 'bg-neutral-50 border-neutral-200'}`}>
								<div className="flex items-center space-x-2 mb-1">
									<span className={`text-[10px] font-mono tracking-widest uppercase ${isDark ? 'text-neutral-500' : 'text-neutral-500'}`}>FOLLOWING</span>
								</div>
								<p className={`text-2xl font-mono font-bold ${isDark ? 'text-neutral-200' : 'text-neutral-800'}`}>
									{socialData.tu_followingCount?.toLocaleString() || '0'}
								</p>
							</div>
							<div className={`p-4 border rounded-none ${isDark ? 'bg-[#0a0a0a] border-[#222]' : 'bg-neutral-50 border-neutral-200'}`}>
								<div className="flex items-center space-x-2 mb-1">
									<span className={`text-[10px] font-mono tracking-widest uppercase ${isDark ? 'text-neutral-500' : 'text-neutral-500'}`}>VIDEOS</span>
								</div>
								<p className={`text-2xl font-mono font-bold ${isDark ? 'text-neutral-200' : 'text-neutral-800'}`}>
									{socialData.tu_mediaCount?.toLocaleString() || '0'}
								</p>
							</div>
							{socialData.tu_personId && (
								<div
									onClick={() => navigate(`/stalker/persons/${socialData.tu_personId}`)}
									className={`p-4 border rounded-none cursor-pointer flex flex-col justify-between transition-colors ${isDark ? 'bg-[#0a0a0a] border-[#222] hover:border-emerald-500/30' : 'bg-neutral-50 border-neutral-200 hover:border-emerald-500/30'}`}
								>
									<div className="flex items-center space-x-2 mb-2">
										<span className={`text-[10px] font-mono tracking-widest uppercase ${isDark ? 'text-neutral-500' : 'text-neutral-500'}`}>ENTITY_REF</span>
									</div>
									<div className="flex items-center space-x-3">
										{socialData.person_avatar ? (
											<img
												src={getStaticFileUrl(socialData.person_avatar) || ''}
												alt="AVT"
												className={`w-8 h-8 object-cover border ${isDark ? 'border-[#333]' : 'border-neutral-200'}`}
											/>
										) : (
											<div className={`w-8 h-8 flex items-center justify-center border ${isDark ? 'bg-[#1a1a1a] border-[#333]' : 'bg-neutral-100 border-neutral-200'}`}>
												<User className={`w-4 h-4 ${isDark ? 'text-neutral-700' : 'text-neutral-300'}`} />
											</div>
										)}
										<div className="min-w-0 flex-1">
											<p className={`text-[11px] font-mono font-bold uppercase truncate ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>
												{socialData.person_name} {socialData.person_surname}
											</p>
										</div>
									</div>
								</div>
							)}
						</div>

						{/* Bio - TikTok */}
						{socialData.tu_bio && (
							<div className="mt-8">
								<h3 className={`text-[10px] font-mono tracking-widest uppercase mb-2 ${isDark ? 'text-emerald-500' : 'text-emerald-700'}`}>// BIO_DECRYPTED</h3>
								<p className={`text-[11px] font-mono leading-relaxed ${isDark ? 'text-neutral-400' : 'text-neutral-600'}`}>{socialData.tu_bio}</p>
							</div>
						)}
					</>
				) : isVsco ? (
					<>
						{/* Header - VSCO */}
						<div className="flex items-start justify-between flex-col md:flex-row gap-4 border-b pb-6 mb-6 border-dashed border-[#333]">
							<div className="flex items-center space-x-6">
								{socialData.vu_avatarUrl ? (
									<img
										src={socialData.vu_avatarUrl || ''}
										alt={socialData.vu_username}
										className={`w-20 h-20 object-cover border cursor-pointer hover:opacity-80 transition-all ${isDark ? 'border-[#444]' : 'border-neutral-300'}`}
										onClick={() => handleLightboxOpen(socialData.vu_avatarUrl || '', 'image')}
									/>
								) : (
									<div className={`w-20 h-20 flex items-center justify-center border ${isDark ? 'bg-[#1a1a1a] border-[#333]' : 'bg-neutral-100 border-neutral-200'}`}>
										<ImageLucide className={`w-10 h-10 ${isDark ? 'text-neutral-700' : 'text-neutral-300'}`} />
									</div>
								)}
								<div>
									<div className="flex items-center space-x-2">
										<h2 className={`text-xl md:text-2xl font-mono font-bold tracking-widest uppercase ${isDark ? 'text-neutral-200' : 'text-neutral-800'}`}>
											@{socialData.vu_username}
										</h2>
									</div>
									{socialData.vu_name && (
										<p className={`text-[12px] font-mono mt-1 ${isDark ? 'text-emerald-500/70' : 'text-emerald-600/70'}`}>{socialData.vu_name}</p>
									)}
								</div>
							</div>
							{socialData.vu_profileLink && (
								<div className="flex items-center space-x-2">
									<a
										href={socialData.vu_profileLink}
										target="_blank"
										rel="noopener noreferrer"
										className={`flex items-center justify-center w-10 h-10 border transition-all ${isDark ? 'bg-[#0a0a0a] border-[#333] hover:border-emerald-500/50 text-neutral-500' : 'bg-neutral-50 border-neutral-300 hover:border-emerald-500/50 text-neutral-600'}`}
									>
										<ExternalLink className="w-4 h-4" />
									</a>
								</div>
							)}
						</div>

						{/* Person - VSCO */}
						{socialData.vu_personId && (
							<div
								onClick={() => navigate(`/stalker/persons/${socialData.vu_personId}`)}
								className={`p-4 border rounded-none cursor-pointer flex flex-col justify-between transition-colors mt-6 ${isDark ? 'bg-[#0a0a0a] border-[#222] hover:border-emerald-500/30' : 'bg-neutral-50 border-neutral-200 hover:border-emerald-500/30'}`}
							>
								<div className="flex items-center space-x-2 mb-2">
									<span className={`text-[10px] font-mono tracking-widest uppercase ${isDark ? 'text-neutral-500' : 'text-neutral-500'}`}>ENTITY_REF</span>
								</div>
								<div className="flex items-center space-x-3">
									{socialData.person_avatar ? (
										<img
											src={getStaticFileUrl(socialData.person_avatar) || ''}
											alt="AVT"
											className={`w-10 h-10 object-cover border ${isDark ? 'border-[#333]' : 'border-neutral-200'}`}
										/>
									) : (
										<div className={`w-10 h-10 flex items-center justify-center border ${isDark ? 'bg-[#1a1a1a] border-[#333]' : 'bg-neutral-100 border-neutral-200'}`}>
											<User className={`w-5 h-5 ${isDark ? 'text-neutral-700' : 'text-neutral-300'}`} />
										</div>
									)}
									<div className="min-w-0 flex-1">
										<p className={`text-[12px] font-mono font-bold uppercase truncate ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>
											{socialData.person_name} {socialData.person_surname}
										</p>
									</div>
								</div>
							</div>
						)}

						{/* Bio - VSCO */}
						{socialData.vu_bio && (
							<div className="mt-8">
								<h3 className={`text-[10px] font-mono tracking-widest uppercase mb-2 ${isDark ? 'text-emerald-500' : 'text-emerald-700'}`}>// BIO_DECRYPTED</h3>
								<p className={`text-[11px] font-mono leading-relaxed ${isDark ? 'text-neutral-400' : 'text-neutral-600'}`}>{socialData.vu_bio}</p>
							</div>
						)}
					</>
				) : (
					<div className={`text-[11px] font-mono tracking-widest uppercase ${isDark ? 'text-neutral-500' : 'text-neutral-400'}`}>
						[ PLATFORM_OPTIMIZATION_PENDING ]
					</div>
				)}
			</div>

			{/* Media Archive (Matched with Person Detail aesthetic) */}
			<div className={`mt-4 border p-6 ${isDark ? 'bg-[#111] border-[#222]' : 'bg-white border-neutral-200'}`}>
				<div className={`flex justify-between items-center mb-6 pb-2 border-b ${isDark ? 'border-[#222]' : 'border-neutral-200'}`}>
					<h3 className={`text-[10px] font-mono tracking-widest uppercase ${isDark ? 'text-emerald-500' : 'text-emerald-700'}`}>
						// MEDIA_ARCHIVE ({medias.length})
					</h3>
				</div>
				
				{mediaLoading && !medias.length ? (
					<div className="flex items-center justify-center h-32">
						<p className={`font-mono text-[10px] animate-pulse ${isDark ? 'text-neutral-500' : 'text-neutral-400'}`}>[ RETRIEVING_MEDIA_STREAM... ]</p>
					</div>
				) : medias.length ? (
					<div className="grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-4">
						{medias.map((media) => {
							const mediaUrl = media.sm_filePath
								? getStaticFileUrl(media.sm_filePath)
								: media.sm_originalUrl
									? getProxyUrl(media.sm_originalUrl)
									: null;
							return (
								<div key={media.sm_id} className={`p-2 border relative group ${isDark ? 'bg-[#0a0a0a] border-[#222]' : 'bg-neutral-50 border-neutral-200'}`}>
									{mediaUrl && (
										<div className="mb-2 relative">
											{media.sm_mediaType === 'video' ? (
												<div className={`relative w-full h-32 md:h-40 border overflow-hidden cursor-pointer hover:opacity-80 transition-opacity ${isDark ? 'border-[#333]' : 'border-neutral-300'}`} 
													onClick={(e) => {
														e.stopPropagation();
														handleLightboxOpen(mediaUrl, 'video', media);
													}}>
													<video 
														src={mediaUrl} 
														className="w-full h-full object-cover" 
														preload="metadata"
													/>
													<div className="absolute inset-0 flex items-center justify-center bg-black/40">
														<Play className="w-8 h-8 text-neutral-300" fill="currentColor" />
													</div>
												</div>
											) : (
												<div className={`w-full h-32 md:h-40 border overflow-hidden ${isDark ? 'border-[#333]' : 'border-neutral-300'}`}>
													<img 
														src={mediaUrl} 
														alt={media.sm_fileName || 'Media'} 
														className="w-full h-full object-cover cursor-pointer transition-opacity duration-300"
														onClick={() => handleLightboxOpen(mediaUrl, 'image', media)}
													/>
												</div>
											)}
											
											{/* Action Overlay */}
											<div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-start items-end p-2 gap-2 pointer-events-none">
												<div className="pointer-events-auto flex items-center space-x-1">
													<button
														onClick={async (e) => {
															e.stopPropagation();
															try {
																const response = await fetch(mediaUrl);
																const blob = await response.blob();
																const url = window.URL.createObjectURL(blob);
																const link = document.createElement('a');
																link.href = url;
																link.download = media.sm_fileName || 'media';
																document.body.appendChild(link);
																link.click();
																document.body.removeChild(link);
																window.URL.revokeObjectURL(url);
															} catch (error) {
																console.error('Download error:', error);
															}
														}}
														className={`p-1.5 border transition-colors ${isDark ? 'bg-black/80 border-[#333] hover:bg-[#222] text-neutral-400 hover:text-white' : 'bg-white/90 border-neutral-300 hover:bg-neutral-100 text-neutral-600 hover:text-black'}`}
														title="Download"
													>
														<Download className="w-3.5 h-3.5" />
													</button>
												</div>
											</div>
										</div>
									)}
									<div className="mt-2 space-y-1">
										<p className={`font-mono text-[9px] tracking-widest font-bold uppercase ${isDark ? 'text-neutral-500' : 'text-neutral-400'}`}>
											// ID: {media.sm_id}
										</p>
										{media.created_at && (
											<p className={`font-mono text-[8px] uppercase tracking-wider ${isDark ? 'text-neutral-600' : 'text-neutral-500'}`}>
												[{new Date(media.created_at).toLocaleString('tr-TR', { dateStyle: 'short' })}]
											</p>
										)}
									</div>
								</div>
							);
						})}
					</div>
				) : (
					<p className={`font-mono text-[10px] ${isDark ? 'text-neutral-600' : 'text-neutral-400'}`}>[ NO_RECOGNIZED_MEDIA_FOUND ]</p>
				)}
				
				{/* Infinite scroll sentinel */}
				<div ref={mediaLoadSentinelRef} className="h-4" />
				{mediaLoading && medias.length > 0 && (
					<div className="flex justify-center py-4">
						<p className={`font-mono text-[9px] animate-pulse ${isDark ? 'text-emerald-500/50' : 'text-emerald-700/50'}`}>[ FETCHING_FOLLOWUP_INTEL... ]</p>
					</div>
				)}
				{!mediaHasMore && medias.length > 0 && (
					<p className={`text-center font-mono text-[9px] mt-6 tracking-widest uppercase ${isDark ? 'text-neutral-700' : 'text-neutral-400'}`}>// END_OF_RECORD: {medias.length}_ITEMS_INDEXED</p>
				)}
			</div>

			{/* Lightbox */}
			{lightboxImage && (
				<div
					className="fixed inset-0 bg-black/95 flex items-center justify-center z-[9999] p-4 cursor-zoom-out"
					onClick={handleLightboxClose}
					onWheel={handleWheel}
				>
					{/* Download and Close Buttons */}
					<div className="absolute top-4 right-4 flex items-center space-x-3 z-10">
						<button
							onClick={async (e) => {
								e.stopPropagation();
								if (lightboxImage) {
									try {
										const response = await fetch(lightboxImage);
										const blob = await response.blob();
										const url = window.URL.createObjectURL(blob);
										const link = document.createElement('a');
										link.href = url;
										link.download = lightboxMedia?.sm_fileName || 'media';
										document.body.appendChild(link);
										link.click();
										document.body.removeChild(link);
										window.URL.revokeObjectURL(url);
									} catch (error) {
										console.error('Download error:', error);
									}
								}
							}}
							className={`p-2 border transition-all ${isDark ? 'bg-black/80 border-neutral-800 text-neutral-400 hover:text-white hover:border-emerald-500/50' : 'bg-white border-neutral-300 text-neutral-600 hover:text-black hover:border-emerald-500/50'}`}
							title="DOWNLOAD"
						>
							<Download className="w-5 h-5" />
						</button>
						<button
							onClick={(e) => {
								e.stopPropagation();
								handleLightboxClose();
							}}
							className={`p-2 border transition-all ${isDark ? 'bg-black/80 border-neutral-800 text-neutral-400 hover:text-white hover:border-emerald-500/50' : 'bg-white border-neutral-300 text-neutral-600 hover:text-black hover:border-emerald-500/50'}`}
							title="CLOSE"
						>
							<X className="w-5 h-5" />
						</button>
					</div>

					{lightboxType === 'image' ? (
						<div
							className="relative max-w-[90vw] max-h-[90vh]"
							onMouseDown={handleMouseDown}
							onMouseMove={handleMouseMove}
							onMouseUp={handleMouseUp}
							onMouseLeave={handleMouseUp}
						>
							<img
								src={lightboxImage}
								alt="Lightbox"
								className="max-w-full max-h-[90vh] object-contain border border-[#333]"
								style={{
									transform: `scale(${zoomLevel}) translate(${imagePosition.x / zoomLevel}px, ${imagePosition.y / zoomLevel}px)`,
									cursor: zoomLevel > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default'
								}}
								onClick={(e) => e.stopPropagation()}
							/>
						</div>
					) : (
						<video
							src={lightboxImage}
							controls
							className="max-w-[90vw] max-h-[90vh] border border-[#333]"
							onClick={(e) => e.stopPropagation()}
						/>
					)}
				</div>
			)}
		</div>
	);
};

export default SocialDetail;

